import moment from 'moment';

import { CompanyFormType, EmployeeFormType } from '~/data/documents';

export enum FormsEntryType {
  Employee = 'EMPLOYEE',
  Company = 'COMPANY',
}

type FormsEntryCommon = {
  id: number;
  created?: Date;
  modified?: Date;
  attachmentId: number;
  attachmentName: string;
  attachmentIsArchived: boolean;
  description?: string;
};

export type EmployeeFormsEntry = FormsEntryCommon & {
  type: FormsEntryType.Employee;
  employeeId: number;
  companyFormId: number;
  attachment?: File | undefined;
};

export type CompanyFormsEntry = FormsEntryCommon & {
  type: FormsEntryType.Company;
  name: string;
  country: string;
  isActive: boolean;
  expires?: Date;
};

export const convertEmployeeFormTypeToEmployeeFormsEntry = (
  data: EmployeeFormType,
): EmployeeFormsEntry => {
  const {
    fEmpFormId,
    fEmpFormEmpId,
    fEmpFormCompFormFK,
    fEmpFormCreatedTime,
    fEmpFormAttachmentId,
    fEmpFormAttachmentName,
    fEmpFormAttachmentIsArchived,
    fEmpFormUpdatedTime,
    fFormAttachment,
    fEmpFormInfo,
  } = data;

  return {
    id: fEmpFormId,
    type: FormsEntryType.Employee,
    employeeId: fEmpFormEmpId,
    companyFormId: fEmpFormCompFormFK,
    created: !!fEmpFormCreatedTime
      ? moment(fEmpFormCreatedTime).toDate()
      : undefined,
    modified: !!fEmpFormUpdatedTime
      ? moment(fEmpFormUpdatedTime).toDate()
      : undefined,
    attachmentId: fEmpFormAttachmentId,
    attachmentName: fEmpFormAttachmentName ?? '',
    description: fEmpFormInfo ?? '',
    attachment: fFormAttachment,
    attachmentIsArchived: fEmpFormAttachmentIsArchived,
  };
};

export const convertCompanyFormTypeToCompanyFormsEntry = (
  data: CompanyFormType,
): CompanyFormsEntry => {
  const {
    fComFormId,
    fComFormCountry,
    fComFormAttachmentId,
    fComFormAttachmentName,
    fComFormAttachmentIsArchived,
    fComFormCreatedTime,
    fComFormDescription,
    fComFormExpirationDate,
    fComFormUpdatedTime,
    fComFormIsActive,
    fComFormName,
  } = data;

  return {
    id: fComFormId,
    type: FormsEntryType.Company,
    created: !!fComFormCreatedTime
      ? moment(fComFormCreatedTime).toDate()
      : undefined,
    modified: !!fComFormUpdatedTime
      ? moment(fComFormUpdatedTime).toDate()
      : undefined,
    attachmentId: fComFormAttachmentId,
    attachmentName: fComFormAttachmentName ?? '',
    attachmentIsArchived: fComFormAttachmentIsArchived,
    country: fComFormCountry,
    name: fComFormName ?? '',
    description: fComFormDescription ?? '',
    expires: !!fComFormExpirationDate
      ? moment(fComFormExpirationDate).toDate()
      : undefined,
    isActive: fComFormIsActive,
  };
};

export const convertEmployeeFormsEntryToSubmission = (
  tableRow: EmployeeFormsEntry,
  isUpdate?: boolean,
): EmployeeFormType => {
  const {
    id,
    created,
    companyFormId,
    description,
    attachmentId,
    attachmentName,
    attachmentIsArchived,
    attachment,
    employeeId,
  } = tableRow;

  const getDocumentDate = (date: Date) =>
    moment(date).format('YYYY-MM-DD').toString();

  return {
    fEmpFormId: id,
    fEmpFormEmpId: employeeId,
    fEmpFormCompFormFK: companyFormId,
    fEmpFormCreatedTime: !isUpdate && created ? getDocumentDate(created) : '',
    fEmpFormUpdatedTime: getDocumentDate(new Date()),
    fEmpFormInfo: description,
    fEmpFormAttachmentId: attachmentId,
    fEmpFormAttachmentName: attachmentName,
    fEmpFormAttachmentIsArchived: attachmentIsArchived,
    fFormAttachment: attachment,
  };
};

export const employeeFormSubmissionToFormData = (
  submission: EmployeeFormType,
): FormData => {
  const {
    fEmpFormId,
    fEmpFormAttachmentIsArchived,
    fEmpFormCompFormFK,
    fEmpFormCreatedTime,
    fEmpFormUpdatedTime,
    fEmpFormAttachmentName,
    fFormAttachment,
    fEmpFormInfo,
    fEmpFormEmpId,
    fEmpFormAttachmentId,
  } = submission;

  const convertDateString = (date: string) => {
    return moment(date).format('YYYY-MM-DD');
  };

  const formData = new FormData();
  formData.append('fEmpFormCompFormFK', fEmpFormCompFormFK.toString());
  formData.append(
    'fEmpFormCreatedTime',
    fEmpFormCreatedTime ? fEmpFormCreatedTime : '',
  );
  formData.append(
    'fEmpFormUpdatedTime',
    fEmpFormUpdatedTime ? convertDateString(fEmpFormUpdatedTime) : '',
  );
  formData.append('fEmpFormInfo', fEmpFormInfo || '');
  formData.append('fEmpFormId', fEmpFormId ? fEmpFormId.toString() : '-1');
  formData.append('fEmpFormEmpId', fEmpFormEmpId.toString());
  formData.append('fEmpFormAttachmentId', fEmpFormAttachmentId.toString());
  formData.append('fEmpFormAttachmentName', fEmpFormAttachmentName || '');
  formData.append(
    'fEmpFormAttachmentIsArchived',
    fEmpFormAttachmentIsArchived ? 'true' : 'false',
  );
  if (fFormAttachment) {
    formData.append('file', fFormAttachment);
  }
  return formData;
};
